import { RouteAppearanceBoundary } from '@/components/theme/RouteAppearanceBoundary';

import React, { Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "@/lib/stripe";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { TestModeProvider } from "@/contexts/TestModeContext";
import { SupportTokenProvider } from "@/contexts/SupportTokenContext";
import { NewVideoModalProvider } from "@/contexts/NewVideoModalContext";
import { CustomAuthProvider } from "@/contexts/CustomAuthContext";
import { TourProvider } from "@/contexts/TourContext";
import { TourOverlay } from "@/components/tour";
import { GlobalSupportTokenModal } from "@/components/support/GlobalSupportTokenModal";
import { GlobalUploadModal } from "@/components/upload/GlobalUploadModal";
import { AppSkeleton } from "@/components/ui/AppSkeleton";
import { RouteLogger } from "@/components/dev/RouteLogger";
import { VideoStatusWatcher } from "@/components/VideoStatusWatcher";
import { useModalCleanup } from "@/hooks/useModalCleanup";
import Dashboard from "./pages/Dashboard";
import Documentation from "./pages/Documentation";
import MyVideos from "./pages/MyVideos";
import MyVideosV2 from "./pages/MyVideosV2";
import VideoDuel from "./pages/VideoDuel";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";
import WebinarList from "./pages/webinar/WebinarList";
import Login from "./pages/Login";
import SupportLogin from "./pages/SupportLogin";
import Signup from "./pages/Signup";
import CheckoutNew from "./pages/CheckoutNew";
import { useParams } from 'react-router-dom';

// Redirect component for old hook-track routes
const HookTrackRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/hook-track/${id}/metrics`} replace />;
};

// Redirect component for old /personalizacao/:videoId routes
function PersonalizacaoRedirect() {
  const { videoId } = useParams();
  return <Navigate to={`/personalizations/${videoId}`} replace />;
}

// Redirect component for old /pastas/:folderId routes
function PastasRedirect() {
  const { folderId } = useParams();
  return <Navigate to={`/meus-videos?folderId=${folderId}`} replace />;
}

// Redirect component for /metricas/:videoId routes to /metrics?videoId= format
function MetricasRedirect() {
  const { videoId } = useParams();
  return <Navigate to={`/metrics?videoId=${videoId}`} replace />;
}
import WebinarWizard from "./pages/webinar/WebinarWizard";
import WebinarRoom from "./pages/webinar/WebinarRoom";
import WebinarInscricao from "./pages/webinar/public/WebinarInscricao";
import WebinarMetrics from "./pages/webinar/WebinarMetrics";
import WebinarSupport from "./pages/webinar/WebinarSupport";
import Finance from "./pages/Finance";
import NewVideo from "./pages/NewVideo";
import Personalization from "./pages/Personalization";
import Checkout from "./pages/Checkout";
import Partners from "./pages/Partners";
import Academy from "./pages/Academy";
import ReferralProgram from "./pages/ReferralProgram";
import Members from "./pages/Members";
import AuditLogs from "./pages/AuditLogs";
import Tracking from "./pages/Tracking";
import Security from "./pages/Security";
import VSLCloaker from "./pages/VSLCloaker";
import DomainProtection from "./pages/DomainProtection";
import ApiKeys from "./pages/ApiKeys";
import Integrations from "./pages/Integrations";
import MasterClass from "./pages/MasterClass";
import MasterClassSerie from "./pages/masterclass/MasterClassSerie";
import HookTrack from "./pages/HookTrack";
import HookTrackList from "./pages/hook-track/HookTrackList";
import HookTrackCreate from "./pages/hook-track/HookTrackCreate";
import HookTrackTestDetails from "./pages/hook-track/HookTrackTestDetails";
import HookTrackMetrics from "./pages/hook-track/HookTrackMetrics";
import ABTest from "./pages/ABTest";
import ABTestList from "./pages/ab-test/ABTestList";
import ABTestCreate from "./pages/ab-test/ABTestCreate";
import ABTestDetails from "./pages/ab-test/ABTestDetails";
import ABTestMetrics from "./pages/ab-test/ABTestMetrics";
import VSLeads from "./pages/VSLeads";
import VSLeadsCreate from "./pages/vsleads/VSLeadsCreate";
import VSLeadsIntegration from "./pages/vsleads/VSLeadsIntegration";
import VSLeadsEdit from "./pages/vsleads/VSLeadsEdit";
import VSLeadsMetrics from "./pages/vsleads/VSLeadsMetrics";
import EmailCaptureResource from "./pages/vsleads/EmailCaptureResource";
import Metrics from "./pages/Metrics";
import FlowsListPage from "./pages/vsl-dinamica/FlowsListPage";
import FlowMetricsPage from "./pages/vsl-dinamica/FlowMetricsPage";
// Auth page removed - Login/Signup used directly
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import Health from "./pages/Health";
import Pixel from "./pages/Pixel";
import { ShareVideoList, ShareLanding, ShareVideoDetails } from "./pages/share";
import Install from "./pages/Install";
import Novidades from "./pages/Novidades";
import NotificationSettings from "./pages/NotificationSettings";
import { InstallPrompt } from "./components/pwa/InstallPrompt";
import { NotificationPrompt } from "./components/notifications/NotificationPrompt";
import { clearExpiredCache } from "@/utils/thumbnailCache";

// Playlist routes
import PlaylistList from "./pages/playlist/PlaylistList";
import PlaylistCreate from "./pages/playlist/PlaylistCreate";
import PlaylistEdit from "./pages/playlist/PlaylistEdit";
import PlaylistAddVideos from "./pages/playlist/PlaylistAddVideos";
import PlaylistPlayer from "./pages/playlist/PlaylistPlayer";
import PlaylistTemplatePreview from "./pages/playlist/PlaylistTemplatePreview";

// Dark Members routes
import DarkMembersHome from "./pages/members/DarkMembersHome";
import DarkMembersModule from "./pages/members/DarkMembersModule";
import DarkMembersLesson from "./pages/members/DarkMembersLesson";
import DarkMembersModulePreview from "./pages/playlist/DarkMembersModulePreview";

//Afiliates
// import AfiliateReceiveAccount from "./pages/afilliate/ReceiveAccount";
import AfiliateReceiveAccount from "./pages/afilliate/PaymentMethods";
import NetworkList from "./pages/afilliate/NetworkList";
import Payouts from "./pages/afilliate/Payouts";
import Afiliate from "./pages/afilliate/Afiliate";
//Admin
import AdminSummary from "./pages/afilliate/AdmSummary";
import AdminUserTools from "./pages/admin/usertools/AdminUserTools";
import IncrementClicks from "./pages/admin/usertools/IncrementClicks";
import UpdatePayments from "./pages/afilliate/UpdatePayment";
import Costs from "./pages/costs";
import UserCostAnalysis from "./pages/user-cost-analysis";

// Página de Audiência em Tempo Real — lazy para isolar o maplibre num chunk próprio.
const RealtimeAudience = React.lazy(() => import("./pages/audience/RealtimeAudience"));
// Editor da VSL Dinâmica: tela cheia, fora do Layout, carregado sob demanda.
const FlowEditorPage = React.lazy(() => import("./pages/vsl-dinamica/FlowEditorPage"));

// Create a new QueryClient instance
const queryClient = new QueryClient();

// Root redirect component - always go to dashboard
const RootRedirect = () => {
  return <Navigate to="/dashboard" replace />;
};

// Wrap the App component in React.StrictMode
const App = () => {
  console.log('App component rendered, setting up routes');

  // Fix modal blocking clicks after close
  useModalCleanup();

  // Cleanup service workers in preview/lovable environments (defensive)
  useEffect(() => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      const isLovablePreview = window.location.hostname.includes('lovable.app');
      const isDev = import.meta.env.DEV;

      if (isLovablePreview || isDev) {
        navigator.serviceWorker.getRegistrations()
          .then(registrations => {
            registrations.forEach(reg => {
              console.log('🧹 Removendo Service Worker:', reg.scope);
              reg.unregister();
            });
          })
          .catch(err => console.error('Erro ao limpar Service Workers:', err));
      }
    }
  }, []);

  // Cleanup expired thumbnail cache on app start
  useEffect(() => {
    clearExpiredCache().catch(() => { });
  }, []);

  // Captura o seller (id_Stripe_Client do indicador) da URL e persiste no localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sellerId = urlParams.get('id');
    if (sellerId) {
      localStorage.setItem('seller_id', sellerId);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CustomAuthProvider>
        <Elements stripe={stripePromise}>
          <ThemeProvider>
            <TestModeProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  {import.meta.env.DEV && <RouteLogger />}
                  <SupportTokenProvider>
                    <NewVideoModalProvider>
                      <TourProvider>
                        <RouteAppearanceBoundary>
                        <Routes>
                          {/* Root route - Login page as default */}
                          <Route path="/" element={<Login />} />

                          {/* Login route - redirect to root */}
                          <Route path="/login" element={<Navigate to="/" replace />} />

                          {/* Cadastro/Signup route */}
                          <Route path="/cadastro" element={<Signup />} />

                          {/* Support login route */}
                          <Route path="/support/login" element={<SupportLogin />} />

                          {/* Documentação - guia técnico para devs */}
                          <Route path="/documentacao" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />

                          {/* Dashboard route - render MyVideosV2 (novo Dashboard) */}
                          <Route path="/dashboard" element={<ProtectedRoute><MyVideosV2 /></ProtectedRoute>} />

                          <Route path="/meus-videos" element={<ProtectedRoute><MyVideos /></ProtectedRoute>} />
                          <Route path="/comparar-vsl" element={<ProtectedRoute><VideoDuel /></ProtectedRoute>} />
                          {/* Rota /meus-videos-v2 removida - agora é a rota principal "/" */}
                          {/* Redirect old /pastas routes to /meus-videos */}
                          <Route path="/pastas" element={<Navigate to="/meus-videos" replace />} />
                          <Route path="/pastas/:folderId" element={<PastasRedirect />} />
                          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                          <Route path="/checkout-new" element={<CheckoutNew />} />
                          <Route path="/finance" element={<ProtectedRoute><Finance /></ProtectedRoute>} />
                          <Route path="/new-video" element={<Navigate to="/" replace />} />
                          <Route path="/personalizations" element={<ProtectedRoute><Personalization /></ProtectedRoute>} />
                          <Route path="/personalizations/:videoId" element={<ProtectedRoute><Personalization /></ProtectedRoute>} />
                          {/* Redirect old /personalizacao routes to /personalizations */}
                          <Route path="/personalizacao" element={<Navigate to="/personalizations" replace />} />
                          <Route path="/personalizacao/:videoId" element={<PersonalizacaoRedirect />} />

                          {/* Tools routes */}
                          <Route path="/vsl-dinamica" element={<ProtectedRoute><FlowsListPage /></ProtectedRoute>} />
                          <Route path="/vsl-dinamica/:flowId/metricas" element={<ProtectedRoute><FlowMetricsPage /></ProtectedRoute>} />
                          <Route path="/vsl-dinamica/:flowId" element={<ProtectedRoute><Suspense fallback={<AppSkeleton />}><FlowEditorPage /></Suspense></ProtectedRoute>} />
                          <Route path="/vsl-dynamic/*" element={<Navigate to="/vsl-dinamica" replace />} />
                          <Route path="/vsleads" element={<ProtectedRoute><VSLeads /></ProtectedRoute>} />
                          <Route path="/vsleads/criar" element={<ProtectedRoute><VSLeadsCreate /></ProtectedRoute>} />
                          <Route path="/vsleads/create" element={<ProtectedRoute><VSLeadsCreate /></ProtectedRoute>} />
                          <Route path="/vsleads/email-capture" element={<ProtectedRoute><EmailCaptureResource /></ProtectedRoute>} />
                          <Route path="/vsleads/integracao/:id" element={<ProtectedRoute><VSLeadsIntegration /></ProtectedRoute>} />
                          <Route path="/vsleads/editar/:id" element={<ProtectedRoute><EmailCaptureResource /></ProtectedRoute>} />
                          <Route path="/vsleads/:videoId/metrics" element={<ProtectedRoute><VSLeadsMetrics /></ProtectedRoute>} />
                          <Route path="/metrics" element={<ProtectedRoute><Metrics /></ProtectedRoute>} />
                          <Route path="/tempo-real" element={<ProtectedRoute><Suspense fallback={<AppSkeleton />}><RealtimeAudience /></Suspense></ProtectedRoute>} />
                          {/* Redirect /metricas/:videoId to /metrics?videoId= format */}
                          <Route path="/metricas/:videoId" element={<MetricasRedirect />} />

                          {/* AB Test routes */}
                          <Route path="/ab-test" element={<ProtectedRoute><ABTest /></ProtectedRoute>} />
                          <Route path="/ab-test/list" element={<ProtectedRoute><ABTestList /></ProtectedRoute>} />
                          <Route path="/ab-test/create" element={<ProtectedRoute><ABTestCreate /></ProtectedRoute>} />
                          <Route path="/ab-test/:id" element={<ProtectedRoute><ABTestDetails /></ProtectedRoute>} />
                          <Route path="/ab-test/:id/metrics" element={<ProtectedRoute><ABTestMetrics /></ProtectedRoute>} />

                          {/* HookTrack routes */}
                          <Route path="/hook-track" element={<ProtectedRoute><HookTrack /></ProtectedRoute>} />
                          <Route path="/hook-track/list" element={<ProtectedRoute><HookTrackList /></ProtectedRoute>} />
                          <Route path="/hook-track/create" element={<ProtectedRoute><HookTrackCreate /></ProtectedRoute>} />
                          <Route path="/hook-track/:id" element={<HookTrackRedirect />} />
                          <Route path="/hook-track/:id/metrics" element={<ProtectedRoute><HookTrackMetrics /></ProtectedRoute>} />

                          {/* Webinar routes */}
                          <Route path="/webinarios" element={<ProtectedRoute><WebinarList /></ProtectedRoute>} />
                          <Route path="/webinarios/criar" element={<ProtectedRoute><WebinarWizard /></ProtectedRoute>} />
                          <Route path="/webinarios/editar/:id" element={<ProtectedRoute><WebinarWizard /></ProtectedRoute>} />
                          <Route path="/webinarios/:id/metricas" element={<ProtectedRoute><WebinarMetrics /></ProtectedRoute>} />
                          <Route path="/webinarios/:id/atendimento" element={<ProtectedRoute><WebinarSupport /></ProtectedRoute>} />

                          {/* Public webinar pages */}
                          <Route path="/w/:slug/inscricao" element={<WebinarInscricao />} />
                          <Route path="/w/:slug" element={<WebinarRoom />} />

                          <Route path="/tracking" element={<ProtectedRoute><Tracking /></ProtectedRoute>} />
                          <Route path="/pixel" element={<ProtectedRoute><Pixel /></ProtectedRoute>} />
                          <Route path="/master-class" element={<ProtectedRoute><MasterClass /></ProtectedRoute>} />
                          <Route path="/master-class/serie/:serieId" element={<ProtectedRoute><MasterClassSerie /></ProtectedRoute>} />
                          <Route path="/master-class/serie/:serieId/aula/:aulaId" element={<ProtectedRoute><MasterClassSerie /></ProtectedRoute>} />
                          <Route path="/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
                          <Route path="/vsl-cloaker" element={<ProtectedRoute><VSLCloaker /></ProtectedRoute>} />
                          <Route path="/domain-protection" element={<ProtectedRoute><DomainProtection /></ProtectedRoute>} />
                          <Route path="/api-keys" element={<ProtectedRoute><ApiKeys /></ProtectedRoute>} />
                          <Route path="/partners" element={<ProtectedRoute><Partners /></ProtectedRoute>} />
                          <Route path="/academy" element={<ProtectedRoute><Academy /></ProtectedRoute>} />
                          {/* Novidades is now a modal, not a page */}

                          {/* Share Video routes */}
                          <Route path="/shared" element={<ProtectedRoute><ShareVideoList /></ProtectedRoute>} />
                          <Route path="/distribuir" element={<ProtectedRoute><ShareVideoList /></ProtectedRoute>} />
                          <Route path="/distribuir/:videoId" element={<ProtectedRoute><ShareVideoDetails /></ProtectedRoute>} />
                          <Route path="/shared_video/:shareCode" element={<ShareLanding />} />
                          {/* Public share landing page */}
                          <Route path="/s/:shareCode" element={<ShareLanding />} />
                          <Route path="/indique-ganhe" element={<ProtectedRoute><ReferralProgram /></ProtectedRoute>} />
                          <Route path="/members" element={<ProtectedRoute><Members /></ProtectedRoute>} />
                          <Route path="/admin/audit-logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
                          {/* A página antiga editava um estado que não ia a vídeo nenhum. */}
                          <Route path="/botao-acao" element={<Navigate to="/" replace />} />
                          <Route path="/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />

                          {/* Playlist routes */}
                          <Route path="/playlists" element={<ProtectedRoute><PlaylistList /></ProtectedRoute>} />
                          <Route path="/playlists/new" element={<ProtectedRoute><PlaylistCreate /></ProtectedRoute>} />
                          <Route path="/playlists/:id/edit" element={<ProtectedRoute><PlaylistEdit /></ProtectedRoute>} />
                          <Route path="/playlists/:id/add-videos" element={<ProtectedRoute><PlaylistAddVideos /></ProtectedRoute>} />
                          <Route path="/playlist/p/:hash" element={<PlaylistPlayer />} /> {/* Público */}
                          <Route path="/playlist/preview/:template" element={<PlaylistTemplatePreview />} /> {/* Preview Público */}
                          <Route path="/playlist/preview/:template/:playlistId" element={<PlaylistTemplatePreview />} /> {/* Preview com dados reais */}
                          <Route path="/playlist/preview/dark-members/module/:moduleId" element={<DarkMembersModulePreview />} /> {/* Preview módulo Dark Members */}

                          {/* Dark Members - Área de Membros */}
                          <Route path="/members/:hash" element={<DarkMembersHome />} />
                          <Route path="/members/:hash/module/:moduleId" element={<DarkMembersModule />} />
                          <Route path="/members/:hash/lesson/:lessonId" element={<DarkMembersLesson />} />

                          {/* PWA Installation Page */}
                          <Route path="/install" element={<Install />} />

                          {/* Help routes */}
                          <Route path="/chat" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                          <Route path="/help" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

                          {/* Afiliate routes */}
                          <Route path="/receiveaccount" element={<ProtectedRoute><AfiliateReceiveAccount /></ProtectedRoute>} />
                          <Route path="/networkList" element={<ProtectedRoute><NetworkList /></ProtectedRoute>} />
                          <Route path="/payouts" element={<ProtectedRoute><Payouts /></ProtectedRoute>} />
                          <Route path="/afiliate" element={<ProtectedRoute><Afiliate /></ProtectedRoute>} />
                          {/* ADMIN */}
                          <Route path="/AdminSummary" element={<ProtectedRoute><AdminSummary /></ProtectedRoute>} />
                          <Route path="/AdminUserManager" element={<ProtectedRoute><AdminUserTools /></ProtectedRoute>} />
                          <Route path="/IncrementClicks" element={<ProtectedRoute><IncrementClicks /></ProtectedRoute>} />
                          <Route path="/updatepayment/:id_payout" element={<ProtectedRoute><UpdatePayments /></ProtectedRoute>} />
                          <Route path="/costs" element={<ProtectedRoute><Costs /></ProtectedRoute>} />
                          <Route path="/user-cost-analysis" element={<ProtectedRoute><UserCostAnalysis /></ProtectedRoute>} />
                          {/* Health check */}
                          <Route path="/__health" element={<Health />} />

                          {/* Catch-all route */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                        </RouteAppearanceBoundary>
                        <GlobalUploadModal />
                        <GlobalSupportTokenModal />
                        <InstallPrompt />
                        <VideoStatusWatcher />
                        <TourOverlay />
                      </TourProvider>
                    </NewVideoModalProvider>
                  </SupportTokenProvider>
                </BrowserRouter>
              </TooltipProvider>
            </TestModeProvider>
          </ThemeProvider>
        </Elements>
      </CustomAuthProvider>
    </QueryClientProvider>
  );
};

export default App;
